import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { differenceInDays } from 'date-fns';
import styled from 'styled-components';

import type { CardCollectionScoringMatrix } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Gauge } from '@sorare/core/src/atoms/ui/Gauge';
import { DetailedScoreLine } from '@sorare/core/src/components/collections/CollectionGameHelper/DetailedScoreLine';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';

import type { CollectionBonuses_cardCollectionCard } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 2 })`
  align-self: stretch;
`;
const DaysLeft = styled(LabelS)`
  text-align: right;
  margin-left: auto;
  margin-bottom: var(--half-unit);
`;
const StyledVertical = styled(Vertical).attrs({ gap: 0.5 })`
  padding-top: var(--half-unit);
`;

type Props = {
  cardCollectionCard: CollectionBonuses_cardCollectionCard;
  displayWarning: boolean;
  scoringMatrix: CardCollectionScoringMatrix[];
};

export const CollectionBonuses = ({
  cardCollectionCard,
  displayWarning,
  scoringMatrix,
}: Props) => {
  const { holdingThresholdDays, scoreBreakdown, heldSince } =
    cardCollectionCard;
  const { total, __typename, ...scores } = scoreBreakdown;
  const remainingDays = differenceInDays(new Date(), heldSince);

  return (
    <Root>
      {Object.entries(scores).map(([key, value]) => {
        const showProgression =
          !value &&
          key === 'holding' &&
          remainingDays >= 0 &&
          holdingThresholdDays > 0;
        if (!value && !showProgression) {
          return null;
        }
        const detailedScore = scoringMatrix?.find(
          score => toCamelCase(score.name) === key
        );
        if (!detailedScore) return null;
        return (
          <DetailedScoreLine
            key={key}
            detailedScore={detailedScore}
            listed={displayWarning}
            specificScore={value}
            explanationGauge={
              showProgression && remainingDays >= 0 ? (
                <StyledVertical>
                  <Gauge
                    percentage={`${(remainingDays * 100) / holdingThresholdDays}%`}
                  />
                  <DaysLeft>
                    {Math.ceil(remainingDays)}/{holdingThresholdDays}
                  </DaysLeft>
                </StyledVertical>
              ) : null
            }
          />
        );
      })}
    </Root>
  );
};

CollectionBonuses.fragments = {
  cardCollectionCard: gql`
    fragment CollectionBonuses_cardCollectionCard on CardCollectionCard {
      id
      holdingThresholdDays
      heldSince
      scoreBreakdown {
        firstOwner
        firstSerialNumber
        holding
        owner
        shirtMatchingSerialNumber
        specialEdition
        firstTimeOwnedPlayerCard
        rookie
        total
      }
    }
  ` as TypedDocumentNode<CollectionBonuses_cardCollectionCard>,
};
